import {useCallback} from "react";
import {useDispatch} from "react-redux";
import {cardsActions} from "../cards.slice";
import {CardMarkDto, MAX_CARD_MARK_NOTE_LENGTH, SubCard, subcardKey} from "../../common";
import {useConfirmDialog} from "../../components/ConfirmDialogContext";
import {subcardQuestionLine} from "./markInstructions";

/** Opens the "Mark for regeneration" dialog; for an already marked card it edits the note. */
export function useMarkCardDialog(): (cardId: string, mark?: CardMarkDto) => void {
    const dispatch = useDispatch();
    const {openPromptDialog} = useConfirmDialog();

    return useCallback((cardId: string, mark?: CardMarkDto) => {
        const isMarked = mark?.wholeCard === true;
        openPromptDialog({
            title: isMarked ? 'Edit card mark' : 'Mark card for regeneration',
            message: 'The whole card will show up under "Marked" in Search until you remove the mark.',
            field: {
                label: 'What to change (optional)',
                placeholder: 'For example: add the plural forms, the hint gives the answer away.',
                defaultValue: isMarked ? mark?.note ?? '' : '',
                maxLength: MAX_CARD_MARK_NOTE_LENGTH,
            },
            confirmLabel: isMarked ? 'Save' : 'Mark',
            onConfirm: ({text}) => {
                dispatch(cardsActions.markCard({cardId, note: text.trim()}));
            },
        });
    }, [dispatch, openPromptDialog]);
}

/** Opens the dialog that marks one subcard (or edits its note); the card counts as marked too. */
export function useMarkSubcardDialog(): (cardId: string, subcard: SubCard, mark?: CardMarkDto) => void {
    const dispatch = useDispatch();
    const {openPromptDialog} = useConfirmDialog();

    return useCallback((cardId: string, subcard: SubCard, mark?: CardMarkDto) => {
        const key = subcardKey(subcard.question, subcard.answer);
        const existing = mark?.subcards.find(marked => marked.subcardKey === key);
        openPromptDialog({
            title: existing !== undefined ? 'Edit subcard mark' : 'Mark this subcard',
            message: `«${subcardQuestionLine(subcard.question)}» — the card will show up under "Marked" in Search, with this subcard flagged.`,
            field: {
                label: 'What is wrong with it (optional)',
                placeholder: 'For example: wrong case in the answer, the translation does not match.',
                defaultValue: existing?.note ?? '',
                maxLength: MAX_CARD_MARK_NOTE_LENGTH,
            },
            confirmLabel: existing !== undefined ? 'Save' : 'Mark',
            onConfirm: ({text}) => {
                dispatch(cardsActions.markSubcard({cardId, subcardKey: key, note: text.trim()}));
            },
        });
    }, [dispatch, openPromptDialog]);
}
